import { createSlice } from '@reduxjs/toolkit'
import {
  IsSaveRowKeysAction,
  IsRestoreSelectionAndUpdateQuantities,
  IsSaveArticles,
  IsToggleEditTableAction,
  IsClearTagFiltersValue,
} from '../../Constants/Configuration'
import { quantityLimitDefaultInputChannelName } from '../../Constants/TableConfiguration'
import { IBarcodeAndSizes } from 'Services/Kiosk/BarcodeService'

const emptyArticleDetail = {
  quantities: [],
  marketplaceAvailabilities: [],
  availabilities: [],
  movements: [],
  enabledMarketplaces: [],
  priceData: [],
  quantityLimitData: {} as any,
  descriptions: {} as any,
  pubblicationsite: [],
  basePriceData: [],
  barcodesAndSizes: {} as IBarcodeAndSizes,
  fetchedQuantities: false,
  fetchedMarketplaceAvailabilities: false,
  fetchedAvailabilites: false,
  fetchedMovements: false,
  fetchedEnabledMarketplace: false,
  fetchedPriceData: false,
  fetchedQuantityLimitData: false,
  fetchedDescriptions: false,
  fetchedPubblicationsite: false,
  fetchedBarcodes: false,
}

export const articleDetailSlice = createSlice({
  name: 'articleDetail',
  initialState: {
    articleSelected: emptyArticleDetail,
    limitChangeModal: false,
    isQuantityLimitLoading: false,
  },
  reducers: {
    clearArticleInfoAndSetArticleKey: (state, action) => {
      state.articleSelected = emptyArticleDetail
    },
    toggleLimitChangeModal: (state, action) => {
      state.limitChangeModal = action.payload
    },
    setArticleQuantities: (state, action) => {
      state.articleSelected.quantities = action.payload
      state.articleSelected.fetchedQuantities = true
    },
    setArticleBasePriceData: (state, action) => {
      state.articleSelected.basePriceData = action.payload
    },
    updateBasePriceValue: (state, action) => {
      const { pricelist, SizeId, PriceType, Price } = action.payload
      const listing = state.articleSelected.basePriceData.find(x => x.name === pricelist)
      if (!listing) return
      const priceEntry = listing.prices[SizeId] ?? (!listing.isBySize ? listing.prices[0] : null)
      if (!priceEntry) return
      if (PriceType === 1) {
        priceEntry.sellPrice = Price
      } else {
        priceEntry.buyPrice = Price
      }
    },
    setArticleAvailabilities: (state, action) => {
      state.articleSelected.availabilities = action.payload
      state.articleSelected.fetchedAvailabilites = true
    },
    setArticleMovements: (state, action) => {
      state.articleSelected.movements = action.payload
      state.articleSelected.fetchedMovements = true
    },
    setArticleMarketplaceAvailabilities: (state, action) => {
      state.articleSelected.marketplaceAvailabilities = action.payload
      state.articleSelected.fetchedMarketplaceAvailabilities = true
    },
    setArticleEnabledMarketplace: (state, action) => {
      state.articleSelected.enabledMarketplaces = action.payload
      state.articleSelected.fetchedEnabledMarketplace = true
    },
    setArticlePriceData: (state, action) => {
      state.articleSelected.priceData = action.payload
      state.articleSelected.fetchedPriceData = true
    },
    setArticleQuantityLimitData: (state, action) => {
      state.articleSelected.quantityLimitData = action.payload
      state.articleSelected.fetchedQuantityLimitData = true
    },
    setArticleDescriptions: (state, action) => {
      state.articleSelected.descriptions = action.payload
      state.articleSelected.fetchedDescriptions = true
    },
    setPubblicationsSite: (state, action) => {
      state.articleSelected.pubblicationsite = action.payload
      state.articleSelected.fetchedPubblicationsite = true
    },
    setArticleBarcodesAndSizes: (state, action) => {
      state.articleSelected.barcodesAndSizes = action.payload
      state.articleSelected.fetchedBarcodes = true
    },
    setDelayToSave: (state, action) => {
      if (action.payload.channelName === quantityLimitDefaultInputChannelName) {
        const channelLimitValues = state.articleSelected.quantityLimitData.channelDelays.filter(x => x.delay === -1)

        state.articleSelected.quantityLimitData.defaultDelay = action.payload.delay

        channelLimitValues.forEach(element => {
          element.delay = -1
        })
      } else {
        const channelDelays = state.articleSelected.quantityLimitData.channelDelays
        if (channelDelays && channelDelays.length > 0) {
          const objectToChange = channelDelays.find(x => x.channelName === action.payload.channelName)
          if (objectToChange) {
            objectToChange.delay = action.payload.delay
          }
        }
      }
    },
    setQuantityLimitToSave: (state, action) => {
      if (action.payload.channelName === quantityLimitDefaultInputChannelName) {
        const maxAvailability = state.articleSelected.quantityLimitData.maxAvailability

        if (maxAvailability && maxAvailability.length > 0) {
          const objectToChange = maxAvailability.find(x => x.sizeId === action.payload.sizeId)

          const channelQuantityValues = state.articleSelected.quantityLimitData.channelAvailabilities.filter(
            x => x.sizeId === action.payload.sizeId,
          )

          if (objectToChange) {
            objectToChange.quantity = action.payload.quantity

            channelQuantityValues.forEach(element => {
              element.quantity = -1
            })
          }
        }
      } else {
        const channelAvailabilities = state.articleSelected.quantityLimitData.channelAvailabilities
        if (channelAvailabilities && channelAvailabilities.length > 0) {
          const objectToChange = channelAvailabilities.find(
            x => x.sizeId === action.payload.sizeId && x.channelName === action.payload.channelName,
          )
          if (objectToChange) {
            objectToChange.quantity = action.payload.quantity
          }
        }
      }
    },
    resetMaxQuantityLimitAvailability: state => {
      const maxAvailability = state.articleSelected.quantityLimitData.maxAvailability.map(availability => {
        return availability.quantity !== -1 ? { ...availability, quantity: -1 } : { ...availability }
      })

      const channelAvailabilities = state.articleSelected.quantityLimitData.channelAvailabilities.map(availability => {
        return availability.quantity !== -1 ? { ...availability, quantity: -1 } : { ...availability }
      })

      state.articleSelected.quantityLimitData.maxAvailability = maxAvailability
      state.articleSelected.quantityLimitData.channelAvailabilities = channelAvailabilities
    },
    handleQuantityLimitLoading: (state, action) => {
      state.isQuantityLimitLoading = action.payload
    },
  },
  extraReducers: builder => {
    builder
      .addMatcher(IsSaveRowKeysAction, (state, action) => {
        if (action.payload.rowKeys.length === 0) {
          state.articleSelected = emptyArticleDetail
        }
      })
      .addMatcher(IsRestoreSelectionAndUpdateQuantities, (state, action) => {
        if (action.payload && action.payload.length !== 0) {
          state.articleSelected.quantities = action.payload
        }
      })
      .addMatcher(IsSaveArticles, (state, action) => {
        if (action.payload.results.results.length !== 0 && action.payload.isEditDisable) {
          const isKeySelectedInResult =
            action.payload.results.results.filter(x => action.payload.key === x.key).length !== 0
          if (!isKeySelectedInResult) {
            state.articleSelected = emptyArticleDetail
          }
        }

        if (action.payload.results.results.length === 0 && action.payload.isEditDisable) {
          state.articleSelected = emptyArticleDetail
        }
      })
      .addMatcher(IsToggleEditTableAction, (state, action) => {
        state.articleSelected = emptyArticleDetail
      })
      .addMatcher(IsClearTagFiltersValue, (state, action) => {
        state.articleSelected = emptyArticleDetail
      })
  },
})

// Action creators are generated for each case reducer function
export const {
  toggleLimitChangeModal,
  clearArticleInfoAndSetArticleKey,
  setArticleQuantities,
  setArticleAvailabilities,
  setArticleBasePriceData,
  updateBasePriceValue,
  setArticleMovements,
  setArticleMarketplaceAvailabilities,
  setArticleEnabledMarketplace,
  setArticlePriceData,
  setArticleQuantityLimitData,
  setDelayToSave,
  setQuantityLimitToSave,
  setArticleDescriptions,
  setPubblicationsSite,
  setArticleBarcodesAndSizes,
  resetMaxQuantityLimitAvailability,
  handleQuantityLimitLoading,
} = articleDetailSlice.actions

export default articleDetailSlice.reducer
